/**
 * 评论审核页面 - 销售角色
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Eye, Loader2, Search } from 'lucide-react';
import { api, getErrorMessage } from '../services/api';
import { useToast } from '../components/Toast';

export default function CommentReview() {
  const { showToast } = useToast();
  const [tab, setTab] = useState<'pending' | 'all'>('pending');
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const fetchComments = async () => {
    setLoading(true);
    try {
      const url = tab === 'pending' ? '/promotions/comments/pending' : '/promotions/comments/all';
      const r = await api.request<any[]>(url);
      setComments(r.data || []);
    } catch (e) { showToast('error', getErrorMessage(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchComments(); }, [tab]);

  const handleReview = async (id: number, status: number) => {
    try {
      await api.request(`/promotions/comments/${id}/review`, { method: 'PUT', body: JSON.stringify({ status }) });
      showToast('success', status === 1 ? '已通过' : '已拒绝');
      fetchComments();
      window.dispatchEvent(new CustomEvent('refresh-sidebar-counts'));
    } catch (e) { showToast('error', getErrorMessage(e)); }
  };

  const filtered = search
    ? comments.filter(c => (c.content || '').includes(search) || (c.user_name || '').includes(search) || (c.title || '').includes(search))
    : comments;

  return (
    <div style={{ padding: '16px 24px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700 }}>评论审核</h1>
        <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 8, padding: 3 }}>
          <button onClick={() => setTab('pending')} style={{
            padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '.8125rem', fontWeight: 500,
            background: tab === 'pending' ? '#fff' : 'transparent', color: tab === 'pending' ? 'var(--cinnabar-600)' : 'var(--ink-500)',
            boxShadow: tab === 'pending' ? '0 1px 3px rgba(0,0,0,.1)' : 'none'
          }}>待审核</button>
          <button onClick={() => setTab('all')} style={{
            padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '.8125rem', fontWeight: 500,
            background: tab === 'all' ? '#fff' : 'transparent', color: tab === 'all' ? 'var(--cinnabar-600)' : 'var(--ink-500)',
            boxShadow: tab === 'all' ? '0 1px 3px rgba(0,0,0,.1)' : 'none'
          }}>全部评论</button>
        </div>
      </div>

      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid var(--color-border)', borderRadius: 8, padding: '6px 12px', maxWidth: 320 }}>
        <Search size={16} style={{ color: 'var(--ink-400)' }} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="搜索评论内容或用户..."
          style={{ border: 'none', outline: 'none', flex: 1, fontSize: '.8125rem', background: 'transparent' }} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--ink-400)' }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--ink-400)', fontSize: '.875rem' }}>
          {tab === 'pending' ? '暂无待审核评论' : '暂无评论'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(c => (
            <div key={c.id} style={{
              background: '#fff', border: '1px solid var(--color-border)', borderRadius: 10, padding: '14px 18px',
              display: 'flex', alignItems: 'flex-start', gap: 14
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <strong style={{ fontSize: '.8125rem' }}>{c.user_name}</strong>
                  <span style={{ fontSize: '.6875rem', color: 'var(--ink-400)' }}>评论了</span>
                  <span style={{ fontSize: '.8125rem', color: 'var(--cinnabar-500)', fontWeight: 500 }}>{c.title || '未知视频'}</span>
                  <span style={{
                    fontSize: '.6875rem', padding: '2px 8px', borderRadius: 10,
                    background: c.status === 0 ? '#fef3c7' : c.status === 1 ? '#dcfce7' : '#fee2e2',
                    color: c.status === 0 ? '#92400e' : c.status === 1 ? '#166534' : '#991b1b'
                  }}>{['待审核', '已通过', '已拒绝'][c.status]}</span>
                </div>
                <div style={{ fontSize: '.8125rem', color: 'var(--ink-700)', lineHeight: 1.5 }}>{c.content}</div>
                <div style={{ fontSize: '.6875rem', color: 'var(--ink-300)', marginTop: 4 }}>{new Date(c.create_time).toLocaleString('zh-CN')}</div>
              </div>
              {c.status === 0 && (
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => handleReview(c.id, 1)}
                    style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#166534', cursor: 'pointer', fontSize: '.75rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Check size={14} /> 通过
                  </button>
                  <button onClick={() => handleReview(c.id, 2)}
                    style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b', cursor: 'pointer', fontSize: '.75rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <X size={14} /> 拒绝
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
